/* Drawer — a panel that slides in from the right over the page.
 *
 * For a long list that belongs to something already on screen: the report
 * stays where it was behind it, and closing returns the reader to the same
 * place. A centred modal would cover the very numbers the list explains.
 *
 * Same Radix dialog as Modal, so focus, Escape and scroll-locking behave
 * identically. Full width on a phone.
 */
import type { ReactNode } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface DrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  /** A line under the title — what this list is of. */
  description?: string
  children: ReactNode
  /** Pinned below the body, e.g. a pager. */
  footer?: ReactNode
  /** Defaults to 640px. */
  width?: string
}

export function Drawer({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  width = '640px',
}: DrawerProps) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40 data-[state=open]:animate-overlay-in" />

        <Dialog.Content
          style={{ width }}
          className={cn(
            'fixed inset-y-0 right-0 z-50 max-w-full',
            'flex flex-col bg-surface shadow-3',
            'data-[state=open]:animate-drawer-in',
          )}
          aria-describedby={undefined}
        >
          <header className="flex items-start gap-s3 border-b border-line-2 px-s4 py-s3">
            <div className="min-w-0 flex-1">
              <Dialog.Title className="text-lg font-bold">{title}</Dialog.Title>
              {description ? (
                <p className="mt-0.5 text-sm text-ink-2">{description}</p>
              ) : null}
            </div>
            <Dialog.Close asChild>
              <button
                className="grid h-9 w-9 shrink-0 place-items-center rounded-pill bg-surface-2 text-ink-2 transition-colors hover:bg-hover-2"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </Dialog.Close>
          </header>

          <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>

          {footer ? (
            <div className="border-t border-line-2 px-s4 py-s3">{footer}</div>
          ) : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
